import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  ArrowLeft,
  BookOpen,
  FileText,
  Scale,
  Gavel,
  MapPin,
  ShieldCheck,
  ScrollText,
  CheckSquare,
  ChevronRight,
  Loader2,
} from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";
import DisclaimerBanner from "@/components/DisclaimerBanner";

const CATEGORY_STYLES = {
  "Derechos básicos": { bg: "bg-emerald-50", border: "border-emerald-200", icon: "text-emerald-700", Icon: ShieldCheck },
  "Libertad bajo fianza": { bg: "bg-amber-50", border: "border-amber-200", icon: "text-amber-700", Icon: Scale },
  "Asilo y protección": { bg: "bg-red-50", border: "border-red-200", icon: "text-red-700", Icon: BookOpen },
  "Comparecencia": { bg: "bg-blue-50", border: "border-blue-200", icon: "text-blue-700", Icon: FileText },
  "Apelación": { bg: "bg-purple-50", border: "border-purple-200", icon: "text-purple-700", Icon: Gavel },
  "Notificaciones": { bg: "bg-teal-50", border: "border-teal-200", icon: "text-teal-700", Icon: MapPin },
  "Inicio del proceso": { bg: "bg-zinc-100", border: "border-zinc-200", icon: "text-zinc-700", Icon: ScrollText },
  "Audiencias": { bg: "bg-indigo-50", border: "border-indigo-200", icon: "text-indigo-700", Icon: CheckSquare },
};

export default function Manuales() {
  const navigate = useNavigate();
  const [manuals, setManuals] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/manuals").then((r) => {
      setManuals(r.data.manuals);
      setLoading(false);
    }).catch(() => {
      toast.error("No se pudieron cargar los manuales");
      setLoading(false);
    });
  }, []);

  const startManual = async (manual) => {
    try {
      const starter = `Quiero ayuda con el tema: ${manual.title}${manual.form_code ? " (" + manual.form_code + ")" : ""}. Por favor léeme paso a paso.`;
      const conv = await api.post("/conversations", {
        scenario: "phase2",
        title: manual.title,
      });
      navigate(`/scenario/phase2`, { state: { conversationId: conv.data.id, starter, manual } });
    } catch (e) {
      toast.error("No se pudo iniciar el manual");
    }
  };

  return (
    <div className="min-h-screen bg-zinc-50 pb-28">
      <div className="max-w-3xl mx-auto w-full px-5 pt-6">
        <button
          onClick={() => navigate("/dashboard")}
          className="flex items-center gap-2 text-sm font-semibold text-zinc-600 mb-4 hover:text-zinc-900"
          data-testid="manuales-back"
        >
          <ArrowLeft className="w-4 h-4" />
          Volver
        </button>

        <div className="mb-6">
          <p className="text-xs uppercase tracking-widest font-black text-blue-700 mb-1">
            Centro de Detención · Fase 2
          </p>
          <h1 className="text-3xl sm:text-4xl font-black tracking-tight text-zinc-900 leading-tight">
            Manuales de
            <br />
            Autoayuda
          </h1>
          <p className="text-base text-zinc-700 mt-3 leading-relaxed">
            Elige un tema. Te leemos el manual oficial en voz alta y te guiamos paso a paso, en español.
          </p>
          <div className="mt-4 inline-flex items-center gap-2 bg-blue-50 border border-blue-200 rounded-full px-3 py-1.5">
            <Scale className="w-3.5 h-3.5 text-blue-700" />
            <span className="text-xs font-bold text-blue-800">
              Bajo PBNDS 2011 §&nbsp;6.3 · Law Libraries and Legal Material
            </span>
          </div>
        </div>

        {loading ? (
          <div className="flex justify-center py-12">
            <Loader2 className="w-6 h-6 animate-spin text-zinc-500" />
          </div>
        ) : (
          <div className="grid gap-3">
            {manuals.map((m) => {
              const style = CATEGORY_STYLES[m.category] || CATEGORY_STYLES["Derechos básicos"];
              const { Icon } = style;
              return (
                <button
                  key={m.id}
                  data-testid={`manual-${m.id}`}
                  onClick={() => startManual(m)}
                  className={`w-full bg-white border-2 ${style.border} hover:shadow-lg active:scale-[0.99] transition-all rounded-2xl p-5 text-left group`}
                >
                  <div className="flex items-start gap-4">
                    <div className={`w-12 h-12 rounded-xl ${style.bg} flex items-center justify-center flex-shrink-0`}>
                      <Icon className={`w-6 h-6 ${style.icon}`} strokeWidth={2.5} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <span className={`text-[10px] font-black uppercase tracking-widest ${style.icon}`}>
                          {m.category}
                        </span>
                        {m.form_code && (
                          <span className="text-[10px] font-bold text-zinc-500 bg-zinc-100 px-1.5 py-0.5 rounded">
                            {m.form_code}
                          </span>
                        )}
                      </div>
                      <h3 className="font-black text-lg text-zinc-900 leading-tight">{m.title}</h3>
                      <p className="text-xs text-zinc-500 mt-0.5 mb-2">{m.subtitle}</p>
                      <p className="text-sm text-zinc-700 leading-snug">{m.summary}</p>
                    </div>
                    <ChevronRight className="w-5 h-5 text-zinc-400 mt-3 flex-shrink-0 group-hover:text-zinc-700 group-hover:translate-x-0.5 transition-all" />
                  </div>
                </button>
              );
            })}
          </div>
        )}

        <div className="mt-8 bg-amber-50 border border-amber-200 rounded-xl p-4">
          <p className="text-xs text-amber-900 leading-relaxed">
            <span className="font-bold">Recuerda:</span> el formulario oficial se entrega en INGLÉS a la corte EOIR. Nosotros te ayudamos a entender en español y a transcribir tus datos públicos al inglés. Los documentos de apoyo en otro idioma deben llevar traducción certificada.
          </p>
        </div>
      </div>
      <DisclaimerBanner />
    </div>
  );
}
